<template>
  <div class="parking-info-container">
    <div class="container-fluid py-4">
      <!-- 주차장 선택 -->
      <div class="mb-4">
        <div class="row">
          <div class="col-12">
            <label class="form-label fw-bold">주차장 선택</label>
            <select v-model="selectedAreaSeq" class="form-select form-select-lg" @change="onAreaChange">
              <option value="">-- 주차장을 선택하세요 --</option>
              <option v-for="area in parkingAreas" :key="area.AreaSeq" :value="area.AreaSeq">
                {{ area.AreaName }}
              </option>
            </select>
          </div>
        </div>
      </div>

      <!-- 선택된 주차장 상세 정보 -->
      <div v-if="selectedAreaSeq && currentArea" class="info-section">
        <div class="row g-4">
          <!-- 이미지 슬라이드 (왼쪽) -->
          <div class="col-lg-6 col-12">
            <div class="slide-container">
              <div v-if="areaImages.length > 0" class="carousel-wrapper" @mouseenter="showPreview = true" @mouseleave="showPreview = false">
                <div class="carousel-main">
                  <img 
                    :src="currentImage.ImagePath ? `${apiBase}/${currentImage.ImagePath}` : currentImage"
                    :alt="currentImage.ImageName || '주차장 이미지'"
                    class="carousel-image"
                  />
                </div>
                <div v-if="areaImages.length > 1" class="carousel-controls">
                  <button class="carousel-btn carousel-btn-prev" @click="prevImage" title="이전 이미지" aria-label="이전 이미지">
                    ‹
                  </button>
                  <button class="carousel-btn carousel-btn-next" @click="nextImage" title="다음 이미지" aria-label="다음 이미지">
                    ›
                  </button>
                </div>
                <!-- 썸네일 이미지 -->
                <div v-show="areaImages.length > 1 && showPreview" class="carousel-thumbnails-container">
                  <div class="carousel-thumbnails">
                    <div
                      v-for="(img, idx) in areaImages"
                      :key="idx"
                      class="carousel-thumbnail"
                      :class="{ active: idx === currentImageIndex }"
                      @click="selectThumbnail(idx)"
                      :title="img.ImageName"
                    >
                      <img
                        :src="img.ImagePath ? `${apiBase}/${img.ImagePath}` : ''"
                        :alt="img.ImageName"
                        class="thumbnail-img"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="no-image">
                <div class="no-image-content">
                  <i class="bi bi-image"></i>
                  <p>이미지 없음</p>
                </div>
              </div>
            </div>
          </div>

          <!-- 상세 정보 (오른쪽) -->
          <div class="col-lg-6 col-12">
            <div class="detail-box">
              <h3 class="detail-title">{{ areaDetail.Title || currentArea.AreaName }}</h3>
              <div class="detail-content">
                <p v-if="areaDetail.Content" class="content-text">{{ areaDetail.Content }}</p>
                <p v-else class="text-muted">등록된 상세정보가 없습니다.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 선택 전 안내 -->
      <div v-else class="text-center py-5">
        <p class="text-muted fs-5">주차장을 선택하여 상세 정보를 확인해주세요.</p>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'ParkingAreaInfoView',
  data() {
    return {
      parkingAreas: [],
      selectedAreaSeq: '',
      currentArea: null,
      areaDetail: { Title: '', Content: '' },
      areaImages: [],
      currentImageIndex: 0,
      autoSlideInterval: null,
      loading: false,
      showPreview: false
    }
  },
  computed: {
    currentImage() {
      if (this.areaImages.length === 0) return null
      return this.areaImages[this.currentImageIndex]
    },
    apiBase() {
      return process.env.VUE_APP_API_BASE || ''
    }
  },
  mounted() {
    this.fetchParkingAreas()
  },
  beforeUnmount() {
    this.stopAutoSlide()
  },
  methods: {
    async fetchParkingAreas() {
      this.loading = true
      try {
        const res = await axios.get(`${process.env.VUE_APP_API_BASE}/api/parking-area`)
        this.parkingAreas = res.data
        // 첫 번째 항목 자동 선택
        if (this.parkingAreas.length > 0) {
          this.selectedAreaSeq = this.parkingAreas[0].AreaSeq
          await this.onAreaChange()
        }
      } catch (e) {
        console.error('주차장 목록 로드 실패:', e)
      } finally {
        this.loading = false
      }
    },
    async onAreaChange() {
      this.stopAutoSlide()
      this.currentImageIndex = 0
      this.areaDetail = { Title: '', Content: '' }
      this.areaImages = []

      if (!this.selectedAreaSeq) {
        this.currentArea = null
        return
      }

      this.loading = true
      try {
        // 주차장 정보 조회
        this.currentArea = this.parkingAreas.find(a => a.AreaSeq === parseInt(this.selectedAreaSeq))

        // 상세 정보 조회
        try {
          const detailRes = await axios.get(`${process.env.VUE_APP_API_BASE}/api/parking-area/${this.selectedAreaSeq}/detail`)
          if (detailRes.data) {
            this.areaDetail = {
              Title: detailRes.data.Title || '',
              Content: detailRes.data.Content || ''
            }
          }
        } catch (e) {
          // 상세정보가 없을 수 있음
          this.areaDetail = { Title: '', Content: '' }
        }

        // 이미지 조회
        try {
          const imgRes = await axios.get(`${process.env.VUE_APP_API_BASE}/api/parking-area/${this.selectedAreaSeq}/images`)
          this.areaImages = imgRes.data.images || []
          console.log('[ParkingAreaInfoView] Images loaded:', this.areaImages)
          this.areaImages.forEach(img => {
            console.log(`[Image] name: ${img.ImageName}, path: ${img.ImagePath}, fullUrl: /${img.ImagePath}`)
          })
        } catch (e) {
          // 이미지가 없을 수 있음
          console.error('[ParkingAreaInfoView] Failed to load images:', e)
          this.areaImages = []
        }

        // 이미지가 있으면 자동 슬라이드 시작
        if (this.areaImages.length > 1) {
          this.startAutoSlide()
        }
      } catch (e) {
        console.error('정보 로드 실패:', e)
      } finally {
        this.loading = false
      }
    },
    prevImage() {
      this.stopAutoSlide()
      this.currentImageIndex = (this.currentImageIndex - 1 + this.areaImages.length) % this.areaImages.length
      this.startAutoSlide()
    },
    nextImage() {
      this.stopAutoSlide()
      this.currentImageIndex = (this.currentImageIndex + 1) % this.areaImages.length
      this.startAutoSlide()
    },
    selectThumbnail(idx) {
      this.stopAutoSlide()
      this.currentImageIndex = idx
      this.startAutoSlide()
    },
    startAutoSlide() {
      if (this.areaImages.length <= 1) return
      this.autoSlideInterval = setInterval(() => {
        this.currentImageIndex = (this.currentImageIndex + 1) % this.areaImages.length
      }, 5000)
    },
    stopAutoSlide() {
      if (this.autoSlideInterval) {
        clearInterval(this.autoSlideInterval)
        this.autoSlideInterval = null
      }
    }
  }
}
</script>

<style scoped>
.parking-info-container {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
  padding: 2rem 0;
}

.info-section {
  animation: fadeIn 0.3s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 슬라이드 컨테이너 */
.slide-container {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  height: 400px;
}

.carousel-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
}

.carousel-main {
  width: 100%;
  height: 100%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
  position: relative;
}

.carousel-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: opacity 0.3s ease;
}

/* 썸네일 컨테이너 */
.carousel-thumbnails-container {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.8), transparent);
  padding: 1rem 0.5rem 0.5rem 0.5rem;
  display: flex;
  justify-content: center;
  opacity: 1;
  transition: opacity 0.3s ease;
}

.carousel-thumbnails {
  display: flex;
  gap: 0.75rem;
  overflow-x: auto;
  overflow-y: hidden;
  max-width: 100%;
  padding: 0 0.5rem 0.5rem 0.5rem;
  scroll-behavior: smooth;
}

.carousel-thumbnails::-webkit-scrollbar {
  height: 4px;
}

.carousel-thumbnails::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 2px;
}

.carousel-thumbnails::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.4);
  border-radius: 2px;
}

.carousel-thumbnails::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.6);
}

.carousel-thumbnail {
  flex-shrink: 0;
  width: 90px;
  height: 90px;
  border: 3px solid transparent;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  background: rgba(255, 255, 255, 0.1);
  opacity: 0.7;
}

.carousel-thumbnail:hover {
  opacity: 0.9;
  transform: scale(1.05);
}

.carousel-thumbnail.active {
  border-color: #ffc107;
  opacity: 1;
  box-shadow: 0 0 10px rgba(255, 193, 7, 0.6);
  transform: scale(1.08);
}

.thumbnail-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.no-image {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
}

.no-image-content {
  text-align: center;
  color: #9ca3af;
}

.no-image-content i {
  font-size: 4rem;
  margin-bottom: 1rem;
  opacity: 0.3;
}

.no-image-content p {
  margin: 0;
  font-size: 1.1rem;
}

/* 상세 정보 박스 */
.detail-box {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  height: 100%;
  display: flex;
  flex-direction: column;
  animation: slideInRight 0.3s ease;
}

@keyframes slideInRight {
  from {
    opacity: 0;
    transform: translateX(20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.detail-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 1.5rem;
  border-bottom: 3px solid #3b82f6;
  padding-bottom: 1rem;
}

.detail-content {
  flex: 1;
  overflow-y: auto;
}

.content-text {
  color: #4b5563;
  line-height: 1.8;
  font-size: 1rem;
  white-space: pre-wrap;
  word-break: break-word;
  margin: 0;
}

/* 폼 스타일 */
.form-select {
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  font-size: 1rem;
  transition: all 0.3s ease;
}

.form-select:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-label {
  color: #1f2937;
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
}

/* 반응형 */
@media (max-width: 991px) {
  .slide-container {
    height: 350px;
  }

  .detail-box {
    margin-top: 1.5rem;
  }

  .detail-title {
    font-size: 1.5rem;
  }

  .carousel-btn {
    width: 32px;
    height: 32px;
    font-size: 1.9rem;
  }

  .carousel-btn:hover {
    transform: scale(1.1);
  }

  .carousel-thumbnail {
    width: 75px;
    height: 75px;
  }

  .carousel-thumbnails-container {
    padding: 0.75rem 0.25rem 0.25rem 0.25rem;
  }

  .carousel-thumbnails {
    gap: 0.5rem;
    padding: 0 0.25rem 0.25rem 0.25rem;
  }
}

@media (max-width: 576px) {
  .slide-container {
    height: 280px;
  }

  .carousel-btn {
    width: 28px;
    height: 28px;
    font-size: 1.6rem;
  }

  .carousel-thumbnail {
    width: 65px;
    height: 65px;
    border-width: 2px;
  }

  .carousel-thumbnails-container {
    padding: 0.5rem 0.25rem 0.25rem 0.25rem;
  }

  .carousel-thumbnails {
    gap: 0.4rem;
    padding: 0 0.25rem 0.25rem 0.25rem;
  }

  .detail-title {
    font-size: 1.25rem;
    padding-bottom: 0.75rem;
    margin-bottom: 1rem;
  }

  .content-text {
    font-size: 0.95rem;
  }
}

@media (max-width: 576px) {
  .parking-info-container {
    padding: 1rem 0;
  }

  .slide-container {
    height: 300px;
    border-radius: 8px;
  }

  .carousel-btn {
    width: 40px;
    height: 40px;
    font-size: 1.2rem;
  }

  .detail-box {
    padding: 1.5rem;
    border-radius: 8px;
  }

  .detail-title {
    font-size: 1.3rem;
    margin-bottom: 1rem;
  }

  .carousel-indicators {
    bottom: 0.75rem;
  }

  .indicator {
    width: 8px;
    height: 8px;
  }

  .indicator.active {
    width: 24px;
  }
}
</style>
